<template>
  <v-card :class="'builder-card-' + index">
    <v-card-title
      v-bind:style="{
        color: getQuestionTypeColor(question.questionTypeId)
      }"
    >
      <v-icon
        class="mr-2"
        :color="getQuestionTypeColor(question.questionTypeId)"
      >
        {{ getQuestionTypeIcon(question.questionTypeId) }}
      </v-icon>
      {{ question.questionTypeName }}
      <v-tooltip
        right
        v-if="isOpinionScale"
        :open-on-click="true"
        :open-on-hover="false"
        color="white"
        max-width="400"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn text color="info" v-bind="attrs" v-on="on" @click="on.click">
            What's this?
          </v-btn>
        </template>
        <v-container class="secondarytext--text elevation-5 rounded-lg pa-2">
          <v-row dense>
            <v-col class="font-weight-bold" cols="auto">
              What is an opinion scale?
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="auto">
              An opinion scale is a 0-10 sliding scale that offers the survey
              taker a way to provide a numerical answer to your question. You
              can customize the left and right scale labels. Opinion scale
              questions work best when they’re written as a statement and not as
              a question.
            </v-col>
          </v-row>
        </v-container>
      </v-tooltip>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('moveup')" v-if="index > 0">
        <v-icon>arrow_upward</v-icon>
      </v-btn>
      <v-btn
        icon
        @click="$emit('movedown')"
        v-if="index < totalQuestionCount - 1"
      >
        <v-icon>arrow_downward</v-icon>
      </v-btn>
      <v-btn icon @click="$emit('duplicate')">
        <v-icon>content_copy</v-icon>
      </v-btn>
      <v-btn icon @click="$emit('delete')">
        <v-icon>delete</v-icon>
      </v-btn>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text>
      <v-container>
        <v-row>
          <v-col cols="12">
            <v-text-field
              label="Question"
              v-model="question.question"
              outlined
              :rules="[$rules.required]"
              required
              maxlength="250"
              :disabled="!canSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" v-if="isOpinionScale">
            <v-text-field
              value="0 - 10"
              label="Scale Range"
              outlined
              disabled
              append-icon="lock"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" v-if="isOpinionScale">
            <v-text-field
              v-model="question.scaleLeftLabel"
              label="Left Label"
              outlined
              maxlength="25"
              :rules="[$rules.required]"
              required
              :disabled="!canSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" v-if="isOpinionScale">
            <v-text-field
              v-model="question.scaleRightLabel"
              label="Right Label"
              outlined
              maxlength="25"
              :rules="[$rules.required]"
              required
              :disabled="!canSave"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4" class="pt-0">
            <v-switch
              v-model="question.isRequired"
              color="success"
              hide-details
              class="my-0"
              label="Response required"
            ></v-switch>
          </v-col>
        </v-row>
        <template v-if="isMultipleChoice">
          <v-row
            dense
            v-for="(answer, i) in question.multipleChoiceAnswers"
            :key="i"
          >
            <v-col cols="12" sm="6">
              <v-row dense>
                <v-col cols="8">
                  <v-text-field
                    v-model="answer.answer"
                    :label="'Choice ' + (i + 1)"
                    outlined
                    maxlength="100"
                    :rules="[$rules.required]"
                    required
                    hide-details
                    :disabled="!canSave"
                  ></v-text-field>
                </v-col>
                <v-col cols="4" class="my-auto">
                  <v-btn
                    v-if="question.multipleChoiceAnswers.length > 2 && canSave"
                    icon
                    @click="removeAnswer(i)"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    v-if="i < question.multipleChoiceAnswers.length - 1"
                    @click="moveAnswerDown(i)"
                  >
                    <v-icon>arrow_downward</v-icon>
                  </v-btn>
                  <v-btn icon v-if="i > 0" @click="moveAnswerUp(i)">
                    <v-icon>arrow_upward</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row dense v-if="canSave">
            <v-col cols="12" sm="6" md="4">
              <v-btn
                outlined
                x-large
                color="secondarytext"
                class="border--question border--dashed"
                block
                @click="addAnswer"
              >
                <v-icon color="secondarytext" class="mr-2">add_box</v-icon>
                Add a choice
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import questionTypes from "@/helpers/mixins/questionTypes";
export default {
  name: "SurveyBuilderCard",
  components: {},
  mixins: [questionTypes],
  props: {
    question: { type: Object },
    totalQuestionCount: { type: Number, default: 0 },
    index: { type: Number, default: 0 }
  },
  data: () => ({}),
  computed: {
    canSave() {
      return this.survey.sentInstancesCount == 0 || !this.question.id;
    },
    isOpinionScale() {
      return (
        this.question.questionTypeId == this.$enums.questionTypes.OPINION_SCALE
      );
    },
    isMultipleChoice() {
      return (
        this.question.questionTypeId ==
        this.$enums.questionTypes.MULTIPLE_CHOICE
      );
    },
    ...mapGetters({ survey: "survey/edit_survey" })
  },
  methods: {
    addAnswer() {
      this.question.multipleChoiceAnswers.push({
        answer: ""
      });
    },
    removeAnswer(index) {
      this.question.multipleChoiceAnswers.splice(index, 1);
    },
    moveAnswerUp(index) {
      if (index >= 0) {
        this.question.multipleChoiceAnswers[
          index - 1
        ] = this.question.multipleChoiceAnswers.splice(
          index,
          1,
          this.question.multipleChoiceAnswers[index - 1]
        )[0];
      }
    },
    moveAnswerDown(index) {
      if (index >= 0) {
        this.question.multipleChoiceAnswers[
          index
        ] = this.question.multipleChoiceAnswers.splice(
          index + 1,
          1,
          this.question.multipleChoiceAnswers[index]
        )[0];
      }
    }
  }
};
</script>
